import React from 'react';
import type { VanLifeState, VanLifeAction } from '../../../../games/van-life/van-life';
import { ZONE_CARDS, CHALLENGE_CARDS } from '../../../../games/van-life/van-life';

interface Props {
  state: VanLifeState;
  myId: string;
  isLeader: boolean;
  dispatch: (action: VanLifeAction) => void;
}

export default function MatchResultPanel({ state, myId, isLeader, dispatch }: Props) {
  const match = state.match!;
  const result = match.result!;

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-bold text-amber-200">
        Match {match.matchNumber} Result
      </h2>

      <div className="rounded-xl border border-gray-700 bg-gray-900/50 p-4 text-sm">
        <div className="flex gap-6 mb-3 text-gray-400 text-xs">
          <span>Tracker ended: {result.trackerFinal}</span>
          <span>Deck tricks: {result.deckTrickCount}</span>
        </div>
        <div className="space-y-3">
          {result.parkResults.map(r => {
            const player    = state.players.find(p => p.id === r.playerId)!;
            const park      = state.parks.find(p => p.id === r.parkId)!;
            const zone      = ZONE_CARDS.find(z => z.id === park.zoneCardId)!;
            const challenge = CHALLENGE_CARDS.find(c => c.id === park.challengeCardId)!;
            const isTrack   = player.playerNumber === 1;
            const samePark  = state.players[0].currentParkId === state.players[1].currentParkId;
            return (
              <div key={r.playerId}
                className={`rounded-lg border p-3 ${r.conquered
                  ? 'border-yellow-600 bg-yellow-950/20'
                  : 'border-gray-700 bg-gray-900/30'}`}>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: player.color }} />
                  <span className="font-semibold" style={{ color: player.color }}>{player.name}</span>
                  <span className={`text-xs px-1 py-0.5 rounded ${isTrack ? 'text-blue-400' : 'text-green-400'}`}>
                    ({isTrack ? 'track' : 'challenge'})
                  </span>
                  <span className="text-gray-400 text-xs">@ {park.name}</span>
                  {r.conquered && <span className="ml-auto text-yellow-400 font-bold">★ CONQUERED</span>}
                </div>
                <div className="flex flex-col gap-1 text-xs">
                  {isTrack ? (
                    <div className={r.zoneMet ? 'text-green-400' : 'text-red-400'}>
                      {r.zoneMet ? '✓' : '✗'} Zone{samePark ? '' : ' (required)'}: {zone.name}
                    </div>
                  ) : (
                    <div className={r.challengeMet ? 'text-green-400' : 'text-red-400'}>
                      {r.challengeMet ? '✓' : '✗'} Challenge{samePark ? '' : ' (required)'}: {challenge.name}
                    </div>
                  )}
                  {samePark && (
                    <div className="text-gray-500 text-xs">same park — zone OR challenge counts</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Continue button — leader only */}
      {isLeader ? (
        <button
          onClick={() => dispatch({ type: 'advanceMatch', playerId: myId })}
          className="self-start px-6 py-2 bg-amber-700 hover:bg-amber-600 rounded-lg font-semibold transition-colors">
          {state.matchNumber >= 5 ? 'End Game' : `Continue to Match ${state.matchNumber + 1} →`}
        </button>
      ) : (
        <p className="text-gray-500 text-sm">Waiting for the host to continue…</p>
      )}
    </div>
  );
}
